import { useState, useEffect, useRef, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ToyBrick, BookOpen, Clapperboard, Play, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProductOfferings } from '@/hooks/useProductOfferings';
import {
  mergeLandingSlides,
  type LandingCopyField,
} from '@/lib/landingSlides.mjs';
import { offeringLabel } from '@/lib/productOfferingCtas.mjs';
import './hover-slider-hero.css';

const AVATARS = 'https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/avatars';

type MascotKey = 'riko' | 'umi' | 'bibi' | 'yoyo';
type ChipIconKey = 'toy' | 'book' | 'clapperboard' | 'sparkles';

const LOCKED_META = {
  toy: {
    labelKey: 'heroNav.toy',
    howHash: '#toy',
  },
  story: {
    labelKey: 'heroNav.story',
    howHash: '#story',
  },
  animation: {
    labelKey: 'heroNav.animation',
    howHash: '#animation',
  },
} as const;

const FALLBACK_LOCKED_SLIDES = mergeLandingSlides(
  [
    { productType: 'toy', name: '', nameEn: null, sortOrder: 0 },
    { productType: 'story', name: '', nameEn: null, sortOrder: 1 },
    { productType: 'animation', name: '', nameEn: null, sortOrder: 2 },
  ],
  [],
);

const CHIP_ICONS = {
  toy: ToyBrick,
  book: BookOpen,
  clapperboard: Clapperboard,
  sparkles: Sparkles,
} satisfies Record<ChipIconKey, typeof ToyBrick>;

function chipIcon(icon: ChipIconKey): typeof ToyBrick {
  switch (icon) {
    case 'toy':
      return CHIP_ICONS.toy;
    case 'book':
      return CHIP_ICONS.book;
    case 'clapperboard':
      return CHIP_ICONS.clapperboard;
    case 'sparkles':
      return CHIP_ICONS.sparkles;
    default: {
      const exhaustive: never = icon;
      return exhaustive;
    }
  }
}

const RUBY_IMG = `${AVATARS}/RUBY.png`;
const secondaryCategories = [
  { labelKey: 'heroNav.gallery' as const,     image: RUBY_IMG, link: '/gallery' },
  { labelKey: 'heroNav.howItWorks' as const,  image: RUBY_IMG, link: '/how-it-works' },
  { labelKey: 'heroNav.pricing' as const,     image: RUBY_IMG, link: '/pricing' },
  { labelKey: 'heroNav.reviews' as const,     image: RUBY_IMG, link: '/reviews' },
];

export default function HoverSliderHero() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSecondaryIndex, setActiveSecondaryIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursor2Ref = useRef<HTMLDivElement>(null);
  const cursor3Ref = useRef<HTMLDivElement>(null);
  const [isHoverTarget, setIsHoverTarget] = useState(false);
  const { t, language } = useLanguage();
  const { data: offeringsResult } = useProductOfferings();
  const slides = offeringsResult?.slides ?? FALLBACK_LOCKED_SLIDES;

  useEffect(() => {
    if (activeIndex >= slides.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, slides.length]);

  const tp = (mascot: MascotKey, field: LandingCopyField): string => {
    return t(`heroPanel.${mascot}.${field}`);
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let raf: number;
    const pos = { x: 0, y: 0 };
    const smooth = { x: 0, y: 0 };
    const smooth2 = { x: 0, y: 0 };

    const onMove = (e: MouseEvent) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
    };

    const animate = () => {
      smooth.x += (pos.x - smooth.x) * 0.15;
      smooth.y += (pos.y - smooth.y) * 0.15;
      smooth2.x += (pos.x - smooth2.x) * 0.08;
      smooth2.y += (pos.y - smooth2.y) * 0.08;

      if (cursorRef.current) { cursorRef.current.style.left = `${pos.x}px`; cursorRef.current.style.top = `${pos.y}px`; }
      if (cursor2Ref.current) { cursor2Ref.current.style.left = `${smooth.x}px`; cursor2Ref.current.style.top = `${smooth.y}px`; }
      if (cursor3Ref.current) { cursor3Ref.current.style.left = `${smooth2.x}px`; cursor3Ref.current.style.top = `${smooth2.y}px`; }

      if (root) {
        root.style.setProperty('--mouse-x', `${pos.x}px`);
        root.style.setProperty('--mouse-y', `${pos.y}px`);
      }

      raf = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const handleMouseEnter = useCallback((index: number) => {
    setActiveIndex(index);
    setIsHoverTarget(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoverTarget(false);
  }, []);

  /** First tap switches the mascot video; second tap (already active) navigates. */
  const handleCategoryActivate = useCallback(
    (index: number, event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (index !== activeIndex) {
        event.preventDefault();
        setActiveIndex(index);
        setIsHoverTarget(true);
      }
    },
    [activeIndex],
  );

  const active = slides[activeIndex] ?? slides[0] ?? FALLBACK_LOCKED_SLIDES[0];
  const activeMascot = active.kind === 'locked' ? active.mascot : 'yoyo';
  const activeCopy =
    active.kind === 'custom'
      ? active.copy[language === 'en' ? 'en' : 'fa'] ?? active.copy.fa
      : null;
  const panelCopy = (field: LandingCopyField) =>
    active.kind === 'locked' ? tp(active.mascot, field) : activeCopy?.[field] ?? active.copy.fa[field];
  const ActiveChipIcon = chipIcon(active.chipIcon);
  const activeHowHref =
    active.kind === 'locked'
      ? `/how-it-works${LOCKED_META[active.productType].howHash}`
      : '/how-it-works';
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [activeIndex]);

  return (
    <div className="hover-slider-root" ref={rootRef} data-active-mascot={activeMascot}>

      {/* Reveal background layers */}
      <div className="hero-reveal-layers">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`hero-reveal-slide ${i === activeIndex ? 'show' : ''}`}
            data-mascot={slide.kind === 'locked' ? slide.mascot : 'yoyo'}
          >
            <video
              ref={(el) => { videoRefs.current[i] = el; }}
              className="hero-layer-b"
              src={slide.video}
              autoPlay={i === activeIndex}
              muted
              loop
              playsInline
              preload={i === activeIndex ? 'auto' : 'metadata'}
            />
          </div>
        ))}
      </div>

      {/* 3-column grid */}
      <div className="hero-grid">
        {/* Left column – editorial text panel */}
        <div className="hero-left-col">
          <div className="hero-text-panel" key={active.id}>
            <div className="panel-scrim">
              <span className="hero-chip stagger-0">
                <ActiveChipIcon size={16} strokeWidth={2.25} aria-hidden />
                <span>{panelCopy('chipLabel')}</span>
              </span>

              <h2 className="hero-name stagger-1">
                {panelCopy('name')}<span className="hero-name-dot">.</span>
              </h2>

              <p className="hero-hook stagger-2">
                {panelCopy('hookBefore')}
                <br />
                {panelCopy('hookAfter')}
                <span className="hero-hook-highlight">
                  {panelCopy('hookHighlight')}
                  <svg className="squiggle" viewBox="0 0 300 12" preserveAspectRatio="none" aria-hidden>
                    <path
                      d="M2 8 Q 25 1, 50 6 T 100 6 T 150 6 T 200 6 T 250 6 T 298 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </p>

              <p className="hero-body stagger-3">{panelCopy('body')}</p>

              <div className="hero-cta-row stagger-4">
                <Link to={active.href} className="hero-cta-primary">
                  <Play size={17} strokeWidth={2.5} fill="currentColor" aria-hidden />
                  <span>{panelCopy('ctaLabel')}</span>
                </Link>
                <Link to={activeHowHref} className="hero-cta-secondary">
                  <Sparkles size={17} strokeWidth={2} aria-hidden />
                  <span>{panelCopy('howLabel')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Middle column – category links */}
        <div className="hero-middle-col">
          <ul className="slide-buttons" role="tablist" aria-label={t('heroNav.products')}>
            {slides.map((slide, i) => (
              <li
                key={slide.id}
                className={i === activeIndex ? 'active' : ''}
                role="presentation"
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={slide.href}
                  className="hover-target"
                  role="tab"
                  aria-selected={i === activeIndex}
                  onClick={(event) => handleCategoryActivate(i, event)}
                >
                  {slide.kind === 'locked'
                    ? offeringLabel(
                        { name: slide.navName, nameEn: slide.navNameEn },
                        language,
                        t(LOCKED_META[slide.productType].labelKey),
                      )
                    : slide.navName}
                </Link>
              </li>
            ))}
          </ul>

        </div>

        {/* Right column – empty */}
        <div className="hero-right-col" />

      </div>

      {/* Secondary category row */}
      <div className="hero-grid hero-grid-secondary">
        <div className="hero-left-col" />
        <div className="hero-middle-col">
          <ul className="slide-buttons">
            {secondaryCategories.map((cat, i) => (
              <li
                key={cat.link}
                className={i === activeSecondaryIndex ? 'active' : ''}
                onMouseEnter={() => { setActiveSecondaryIndex(i); setIsHoverTarget(true); }}
                onMouseLeave={handleMouseLeave}
              >
                <Link to={cat.link} className="hover-target">
                  {t(cat.labelKey)}
                </Link>
              </li>
            ))}
          </ul>

        </div>
        <div className="hero-right-col">
          <div className="hero-image-stack">
            {secondaryCategories.map((cat, i) => (
              <Link
                key={cat.labelKey}
                to={cat.link}
                className={`hero-image-item ${i === activeSecondaryIndex ? 'show' : ''}`}
              >
                <img src={cat.image} alt={t(cat.labelKey)} loading="lazy" />
              </Link>
            ))}
          </div>
        </div>
      </div>


      {/* Custom cursors */}
      <div ref={cursorRef} className="cursor" />
      <div ref={cursor2Ref} className={`cursor2 ${isHoverTarget ? 'hover-target' : ''}`} />
      <div ref={cursor3Ref} className="cursor3" />
    </div>
  );
}
